import React from 'react';
import { Link } from 'react-router-dom';

const Disclosures = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F5F3ED', paddingTop: '80px' }}>
      <div className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-5xl font-light mb-8" style={{ fontFamily: 'Georgia, serif', color: '#1a1a3e' }}>
          Investment Disclosures
        </h1>
        
        <div className="bg-white rounded-lg p-8 shadow-sm mb-8">
          <p className="text-sm text-gray-500 mb-8">Last Updated: November 2025</p>
          
          <div className="space-y-6 text-gray-700 leading-relaxed">
            <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-6">
              <p className="font-semibold text-amber-900">
                Important: This document contains important information about risks, fees, and regulatory matters. Please read carefully before investing.
              </p>
            </div>

            <section>
              <h2 className="text-2xl font-light mb-4" style={{ fontFamily: 'Georgia, serif', color: '#1a1a3e' }}>
                1. General Risk Warning
              </h2>
              <p>
                All investments carry risk. The value of your investment may fluctuate, and you may receive back less than your original investment. Past performance is not a reliable indicator of future performance. You should consider your investment objectives, financial situation, and needs before making any investment decision.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-light mb-4" style={{ fontFamily: 'Georgia, serif', color: '#1a1a3e' }}>
                2. Specific Investment Risks
              </h2>
              <ul className="list-disc pl-6 space-y-3">
                <li>
                  <strong>Market Risk:</strong> The value of investments can decrease due to market conditions, economic factors, and investor sentiment.
                </li>
                <li>
                  <strong>Liquidity Risk:</strong> Some investments, particularly private equity, venture capital, and real estate, may not be easily converted to cash. Monthly liquidity is subject to portfolio constraints.
                </li>
                <li>
                  <strong>Concentration Risk:</strong> Portfolios may have significant exposure to specific sectors, asset classes, or geographic regions.
                </li>
                <li>
                  <strong>Currency Risk:</strong> Investments in international assets are subject to exchange rate fluctuations.
                </li>
                <li>
                  <strong>Leverage Risk:</strong> Some portfolio holdings may use leverage, which can amplify both gains and losses.
                </li>
                <li>
                  <strong>Regulatory Risk:</strong> Changes in laws and regulations may impact investment performance.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-light mb-4" style={{ fontFamily: 'Georgia, serif', color: '#1a1a3e' }}>
                3. Fee Structure
              </h2>
              <p className="mb-3">
                Trove charges the following fees:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                <div>
                  <p className="font-semibold">Management Fee: 0.8% - 1.5% per annum</p>
                  <p className="text-sm text-gray-600">Based on portfolio complexity and assets under management. Calculated daily and deducted monthly.</p>
                </div>
                <div>
                  <p className="font-semibold">Performance Fee: 10% - 20%</p>
                  <p className="text-sm text-gray-600">Applied to returns above the specified benchmark. Calculated annually with high-water mark protection.</p>
                </div>
                <div>
                  <p className="font-semibold">Transaction Costs</p>
                  <p className="text-sm text-gray-600">Costs associated with buying, selling, and rebalancing investments are passed through to your account.</p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-light mb-4" style={{ fontFamily: 'Georgia, serif', color: '#1a1a3e' }}>
                4. Portfolio Information
              </h2>
              <p>
                Trove offers multiple portfolio strategies with varying risk/return profiles:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li><strong>Fortress:</strong> Defensive strategy focused on capital preservation (8-10% target return)</li>
                <li><strong>Horizon:</strong> Balanced approach combining growth and stability (10-12% target return)</li>
                <li><strong>Velocity:</strong> Growth-oriented with higher risk tolerance (12-15% target return)</li>
              </ul>
              <p className="mt-3">
                Each portfolio invests across multiple asset classes including public equities, private equity, venture capital, real estate, fixed income, and alternatives.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-light mb-4" style={{ fontFamily: 'Georgia, serif', color: '#1a1a3e' }}>
                5. Redemptions and Liquidity
              </h2>
              <p>
                Redemptions are processed on a monthly basis. You must submit withdrawal requests at least 15 business days before the end of the month. Redemptions are subject to:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>Portfolio liquidity constraints</li>
                <li>Minimum balance requirements</li>
                <li>Potential redemption queues during periods of high withdrawal requests</li>
                <li>Early withdrawal penalties may apply to certain investments</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-light mb-4" style={{ fontFamily: 'Georgia, serif', color: '#1a1a3e' }}>
                6. Regulatory Information
              </h2>
              <p>
                Trove operates under Australian financial services regulations. We are committed to:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>Complying with all ASIC requirements</li>
                <li>Maintaining appropriate AFS licenses</li>
                <li>Following best execution practices</li>
                <li>Implementing robust risk management frameworks</li>
                <li>Conducting regular compliance audits</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-light mb-4" style={{ fontFamily: 'Georgia, serif', color: '#1a1a3e' }}>
                7. Conflicts of Interest
              </h2>
              <p>
                Trove may have conflicts of interest including:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>Receiving fees from portfolio performance</li>
                <li>Trading in securities also held in client portfolios</li>
                <li>Relationships with investment partners and service providers</li>
              </ul>
              <p className="mt-3">
                We manage conflicts through disclosure, segregation of duties, and adherence to our fiduciary obligations.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-light mb-4" style={{ fontFamily: 'Georgia, serif', color: '#1a1a3e' }}>
                8. Tax Considerations
              </h2>
              <p>
                Investment returns may be subject to Australian taxation including capital gains tax, dividend tax, and income tax. We recommend consulting with a qualified tax adviser regarding your specific situation. Trove does not provide tax advice.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-light mb-4" style={{ fontFamily: 'Georgia, serif', color: '#1a1a3e' }}>
                9. Complaint Resolution
              </h2>
              <p>
                If you have a complaint:
              </p>
              <ol className="list-decimal pl-6 space-y-2 mt-3">
                <li>Contact our customer service team at complaints@trove.com.au</li>
                <li>We will acknowledge your complaint within 24 hours</li>
                <li>We aim to resolve complaints within 30 days</li>
                <li>If unresolved, you may escalate to the Australian Financial Complaints Authority (AFCA)</li>
              </ol>
            </section>

            <section>
              <h2 className="text-2xl font-light mb-4" style={{ fontFamily: 'Georgia, serif', color: '#1a1a3e' }}>
                10. Updates to Disclosures
              </h2>
              <p>
                We may update these disclosures from time to time. Material changes will be communicated to all clients. Please review this document regularly.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-light mb-4" style={{ fontFamily: 'Georgia, serif', color: '#1a1a3e' }}>
                11. Contact Information
              </h2>
              <p>
                For questions about these disclosures:
              </p>
              <p className="mt-3">
                <strong>Email:</strong> compliance@trove.com.au<br />
                <strong>Phone:</strong> 1300 TROVE (876 83)<br />
                <strong>Address:</strong> Level 12, 123 Collins Street, Melbourne VIC 3000
              </p>
            </section>
          </div>
        </div>

        <div className="text-center">
          <Link 
            to="/" 
            className="text-[#FF8C42] hover:text-[#FFB366] font-medium"
          >
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Disclosures;